<!-- components/upload/LogoUpload.vue - FIXED -->
<script setup>
import { ref, computed, watch } from "vue";
import {
  PhotoIcon,
  CloudArrowUpIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import { useToast } from "vue-toastification";

// Props & Emits
const props = defineProps({
  modelValue: {
    type: File,
    default: null,
  },
  label: {
    type: String,
    default: "Logo Upload",
  },
});

const emit = defineEmits(["update:modelValue", "upload", "remove"]);

// Composables
const toast = useToast();

// Reactive state
const fileInput = ref(null);
const logoPreview = ref(null);

// Computed
const logoPreviewUrl = computed(() => {
  if (props.modelValue) {
    return URL.createObjectURL(props.modelValue);
  }
  return null;
});

// Watchers
watch(
  () => props.modelValue,
  (newFile) => {
    if (newFile) {
      logoPreview.value = URL.createObjectURL(newFile);
    } else {
      logoPreview.value = null;
    }
  },
  { immediate: true }
);

// Methods
const validateFile = (file) => {
  // Check file type
  const allowedTypes = ["image/jpeg", "image/png", "image/gif"];
  if (!allowedTypes.includes(file.type)) {
    toast.error("Invalid file type. Please upload a JPEG, PNG, or GIF image.");
    return false;
  }

  // Check file size (5MB limit)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    toast.error("File too large. Maximum size is 5MB.");
    return false;
  }

  return true;
};

const triggerFileInput = () => {
  fileInput.value?.click();
};

const handleFileSelect = (event) => {
  const file = event.target.files[0];
  if (file && validateFile(file)) {
    emit("update:modelValue", file);
    emit("upload", file);
    toast.success("Logo uploaded successfully!");
  }
  // Reset input
  event.target.value = "";
};

const handleDrop = (event) => {
  event.preventDefault();
  const file = event.dataTransfer.files[0];
  if (file && validateFile(file)) {
    emit("update:modelValue", file);
    emit("upload", file);
    toast.success("Logo uploaded successfully!");
  }
};

const removeLogo = () => {
  emit("update:modelValue", null);
  emit("remove");
  if (logoPreview.value) {
    URL.revokeObjectURL(logoPreview.value);
    logoPreview.value = null;
  }
};

const formatFileSize = (bytes) => {
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(1)} ${units[unitIndex]}`;
};
</script>

<template>
  <div class="bg-gray-50 p-4 rounded-lg">
    <h3 class="font-semibold text-gray-800 mb-4 flex items-center">
      <PhotoIcon class="w-5 h-5 mr-2" />
      {{props.label}} (Optional)
    </h3>

    <!-- Upload Area -->
    <div
      v-if="!modelValue"
      @click="triggerFileInput"
      @drop="handleDrop"
      @dragover.prevent
      @dragenter.prevent
      class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
    >
      <CloudArrowUpIcon class="w-12 h-12 mx-auto text-gray-400 mb-2" />
      <p class="text-gray-600 mb-1">Click to upload or drag and drop</p>
      <p class="text-sm text-gray-500">PNG, JPG, GIF up to 5MB</p>
    </div>

    <!-- Preview -->
    <div v-else class="space-y-4">
      <div
        class="flex items-center justify-between bg-white p-3 rounded-lg border"
      >
        <div class="flex items-center space-x-3">
          <img
            :src="logoPreviewUrl"
            alt="Logo preview"
            class="w-12 h-12 object-cover rounded"
          />
          <div>
            <p class="font-medium text-gray-800">{{ modelValue.name }}</p>
            <p class="text-sm text-gray-500">
              {{ formatFileSize(modelValue.size) }}
            </p>
          </div>
        </div>
        <button
          @click="removeLogo"
          class="text-red-600 hover:text-red-800 transition-colors"
        >
          <XMarkIcon class="w-5 h-5" />
        </button>
      </div>

      <p class="text-xs text-gray-500">
        Logo will be centered on the QR code at 20% of its size
      </p>
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      @change="handleFileSelect"
      class="hidden"
    />
  </div>
</template>